import React, { useState, useRef, useEffect } from 'react'
import type { CopilotSession } from '../../utils/api'
import { formatRelativeTime } from '../../utils/format'

interface CopilotSessionPanelProps {
  sessions: CopilotSession[]
  activeId: number | string | null
  loading: boolean
  collapsed: boolean
  onToggleCollapse: () => void
  onSelect: (id: number | string) => void
  onNewChat: () => void
  onRename: (id: number | string, title: string) => void
  onDelete: (id: number | string) => void
}

const PlusIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
)

const CollapseIcon: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d={collapsed ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'} />
  </svg>
)

const SessionRow: React.FC<{
  session: CopilotSession
  active: boolean
  onSelect: () => void
  onRename: (title: string) => void
  onDelete: () => void
}> = ({ session, active, onSelect, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(session.title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) {
      setDraft(session.title)
      // focus after render
      requestAnimationFrame(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
      })
    }
  }, [editing, session.title])

  const commit = () => {
    const next = draft.trim()
    setEditing(false)
    if (next && next !== session.title) onRename(next)
  }

  if (editing) {
    return (
      <div className="px-2 py-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setEditing(false)
            }
          }}
          className="w-full px-2 py-1 text-sm rounded-md bg-surface border border-brand-500 text-txt-primary focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>
    )
  }

  return (
    <div
      className={`group/row relative flex items-center rounded-lg transition-colors ${
        active ? 'bg-surface-active' : 'hover:bg-surface-hover'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 min-w-0 text-left px-2.5 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-lg"
      >
        <div className="truncate text-sm text-txt-primary">{session.title || 'New chat'}</div>
        {session.last_message_at && (
          <div className="truncate text-[11px] text-txt-tertiary mt-0.5">
            {formatRelativeTime(session.last_message_at)}
          </div>
        )}
      </button>
      <div className="flex items-center gap-0.5 pr-1.5 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100 transition-opacity">
        <button
          type="button"
          title="Rename"
          aria-label="Rename chat"
          onClick={() => setEditing(true)}
          className="p-1.5 rounded-md text-txt-tertiary hover:text-brand-600 hover:bg-surface-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
          </svg>
        </button>
        <button
          type="button"
          title="Delete"
          aria-label="Delete chat"
          onClick={onDelete}
          className="p-1.5 rounded-md text-txt-tertiary hover:text-error hover:bg-error-light focus:outline-none focus-visible:ring-2 focus-visible:ring-error"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      </div>
    </div>
  )
}

/**
 * Slim, collapsible secondary panel listing the agent's Copilot chats.
 *
 * It always occupies a fixed-width slot in the flex row (whether expanded at
 * w-64 or collapsed to a thin w-12 rail) so toggling never shifts the
 * conversation column. Rename is inline; delete confirms via window.confirm.
 */
export const CopilotSessionPanel: React.FC<CopilotSessionPanelProps> = ({
  sessions,
  activeId,
  loading,
  collapsed,
  onToggleCollapse,
  onSelect,
  onNewChat,
  onRename,
  onDelete,
}) => {
  return (
    <aside
      className={`shrink-0 h-full flex flex-col border-r border-border bg-surface-secondary transition-[width] duration-200 ${
        collapsed ? 'w-12' : 'w-64'
      }`}
    >
      {/* Header / toggle */}
      <div className="flex items-center gap-2 p-2 border-b border-border">
        <button
          type="button"
          onClick={onToggleCollapse}
          title={collapsed ? 'Show chats' : 'Hide chats'}
          aria-label={collapsed ? 'Show chats' : 'Hide chats'}
          className="shrink-0 p-2 rounded-lg text-txt-tertiary hover:text-brand-600 hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <CollapseIcon collapsed={collapsed} />
        </button>
        {!collapsed && (
          <button
            type="button"
            onClick={onNewChat}
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <PlusIcon />
            New chat
          </button>
        )}
      </div>

      {collapsed ? (
        <div className="flex flex-col items-center pt-2">
          <button
            type="button"
            onClick={onNewChat}
            title="New chat"
            aria-label="New chat"
            className="p-2 rounded-lg text-white bg-brand-600 hover:bg-brand-700 shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <PlusIcon />
          </button>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {loading && sessions.length === 0 ? (
            <div className="px-2 py-3 text-xs text-txt-tertiary">Loading chats…</div>
          ) : sessions.length === 0 ? (
            <div className="px-2 py-3 text-xs text-txt-tertiary">No chats yet.</div>
          ) : (
            sessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                active={s.id === activeId}
                onSelect={() => onSelect(s.id)}
                onRename={(title) => onRename(s.id, title)}
                onDelete={() => onDelete(s.id)}
              />
            ))
          )}
        </div>
      )}
    </aside>
  )
}
